import { useState } from 'react'
import { Columns3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export interface ColumnOption {
  id: string
  label: string
}

interface ColumnsPickerProps {
  /** Every toggleable column, in display order. */
  options: ColumnOption[]
  /** Column ids currently hidden. Checked means visible. */
  hidden: Set<string>
  /** Flip one column's visibility. The parent owns the state. */
  onToggle: (id: string) => void
}

/**
 * Toolbar control for column visibility.
 *
 * `ghost` because it sits in a cluster of toolbar actions the toolbar already
 * frames. The popover stays open while columns are toggled, so a user can
 * shape the whole view in one pass.
 */
export function ColumnsPicker({ options, hidden, onToggle }: ColumnsPickerProps) {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5">
          <Columns3 className="h-4 w-4 text-muted-foreground" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-1">
        <div className="space-y-1">
          {options.map((option) => (
            <label
              key={option.id}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent/50"
            >
              <Checkbox
                checked={!hidden.has(option.id)}
                onCheckedChange={() => onToggle(option.id)}
                aria-label={option.label}
              />
              <span className="flex-1 truncate">{option.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
